import React, { useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import { useSelector } from 'store/hooks';
import { trackEvent } from 'helpers/trackerHelpers';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import TableChartIcon from '@mui/icons-material/TableChart';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import Tooltip from '@mui/material/Tooltip';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { useIsEssential, useIsExploreUser } from 'hooks/client';
import { NoColorLink } from 'ui/NoColorLink';
import Flex from 'ui/Flex/Flex';
import ChartContainer from 'components/ChartContainer/ChartContainer';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import ChartEditMenu from 'components/ChartEditMenu/ChartEditMenu';
import vizInfos from 'helpers/vizInfos';
import { isCurrentlyOnPublicDashboard } from 'helpers/dashboardHelpers';
import { DashboardChartT, ChartSettingsT, LayoutT } from 'business/definitions';
import { useGetDashboardChartData } from 'components/ChartContainer/hooks/dashboardChart';
import {
    GetDataMakerResponse,
    GetLayoutChartDataResponse,
} from 'components/ChartContainer/types';
import { MANTY_RED } from 'styles/theme';
import ChartPngPreview from 'components/Shared/ChartPngPreview';
import { usePngExportModalContext } from 'components/ExportMenu/context/PngExportModal/PngExportModalContext';
import TimeFilterCard from 'components/TimeFilterTooltip/TimeFilterCard';
import ConditionalTooltipIconButton from 'components/ConditionalTooltipIconButton';
import { ADMIN, SUPPORT } from 'business/constants';
import { useUserHasRole } from 'hooks/user';
import { useUpdateChartThenUpdateVizSettings } from 'routes/Explore/hooks/updateChartThenUpdateVizSettings';
import MediaCard from './MediaCard';
import GridChartExportMenu from './GridChartExportMenu';
import { ChartActionT } from '../ChartActions/ChartActions';

import './_gridChart.scss';
import PresentationModeContext from '../PresentationModeContext';
import GridChartNoDashboardFilterChip from './NoDashboardFilter/GridChartNoDashboardFilterChip';
import { useGetUnprotectedChartScopes } from './hooks/scopes';
import GridChartNoDashboardFilterModal from './NoDashboardFilter/GridChartNoDashboardFilterModal';
import { useParams } from 'react-router-dom';
import { getPublicToken } from 'components/QueryPerimeter/hooks/useGetTreeData';
import useGetDashboard from 'routes/Explore/hooks/useGetDashboard';
import { getDashboardParamForExploreRoute } from 'routes/Explore/helpers';
import { useQueryClient } from '@tanstack/react-query';

const StyTopLeftWrapper = styled('div', {
    name: 'StyTopLeftWrapper',
})({
    whiteSpace: 'nowrap',
    alignItems: 'center',
    display: 'flex',
});

const StyChartHeaderButtons = styled(Box, {
    name: 'StyChartHeaderButtons',
    shouldForwardProp: (prop) => prop !== 'hover',
})<{ hover: boolean }>(({ hover }) => ({
    background: 'white',
    marginLeft: 'auto',
    transition: 'opacity 0.1s linear',
    display: hover ? 'flex' : 'none',
    flexDirection: 'row',
    '@media print': { display: 'none' },
}));

const StyGridChartCard = styled(Card, {
    name: 'StyGridChartCard',
    shouldForwardProp: (prop) => prop !== 'alertTriggered',
})(({ alertTriggered }: { alertTriggered: boolean }) => {
    // without this, a long pivot chart can overflow its card. A better fix should be applied directly
    // to StyPivotGrid and autosizer, but it requires a global QA.
    // TODO: improve this
    const pivotGridChartOverflowFix = {
        '& ._autosizer_child_div': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
        },
        '& [className^="StyPivotGrid"]': {
            width: '100%',
        },
        '& .pivot-grid-export-container': {
            alignSelf: 'flex-start',
        },
        '& .ag-paging-panel': {
            paddingRight: 30,
        },
    };

    const borderWidth = alertTriggered ? '2px' : '1px';
    const borderColor = alertTriggered ? MANTY_RED : '#efefef';

    return {
        width: '100%',
        padding: '0',
        overflow: 'visible',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        borderStyle: 'solid',
        borderWidth,
        borderColor,
        boxShadow: 'none',
        ...pivotGridChartOverflowFix,
    };
});

type Props = {
    hideLink: boolean;
    canEdit: boolean;
    className: string;
    chart: DashboardChartT;
    children: React.ReactElement<ChartActionT>;
    title: string;
    editMode: boolean;
    toggleEditMode: () => void;
    isPdfModeActivated: boolean;
};

function GridChart({
    chart,
    canEdit,
    hideLink,
    editMode,
    toggleEditMode,
    isPdfModeActivated,
    className,
    title,
    children,
}: Props) {
    const [forceTable, setForceTable] = useState(false);
    const [presentationMode] = React.useContext(PresentationModeContext);
    const [hover, setHover] = useState(false);
    const [noDashboardFiltersModalOpen, setNoDashboardFiltersModalOpen] =
        useState(false);

    const { updateChartThenUpdateVizSettings, isUpdating } =
        useUpdateChartThenUpdateVizSettings(false);

    const { t } = useTranslation();
    const isExploreUser = useIsExploreUser();
    const { slug: slugParam } = useParams();
    const publicToken = getPublicToken();
    const { data: dashboard } = useGetDashboard(slugParam, publicToken);
    const isArchived = dashboard?.isArchived || false;
    const protectedScopes = useGetUnprotectedChartScopes(chart);
    const { showPngExport, setShowPngExport } = usePngExportModalContext();
    const isPublic = dashboard?.orgSharing.isPublic || false;
    const { isAuthenticated } = useSelector((state) => state.vision);
    const isPublicDisplay = isPublic && !isAuthenticated;
    const queryClient = useQueryClient();

    const [rightClickPosition, setRightClickPosition] = React.useState({
        top: 0,
        left: 0,
    });
    const [openContextMenu, setOpenContextMenu] = React.useState(false);
    const onRightClick = (event: React.MouseEvent) => {
        event.preventDefault();
        setRightClickPosition({
            left: event.clientX,
            top: event.clientY,
        });
        setOpenContextMenu(true);
    };

    const [requestTimeFilters, setRequestTimeFilters] = useState<
        GetLayoutChartDataResponse['timeFilters'] | null
    >(null);
    const [chartColumns, setChartColumns] = useState<string[]>([]);

    const getTimeFiltersFromMetadata = useCallback(
        (chartMetadata: GetDataMakerResponse) => {
            if (chartMetadata?.timeFilters) {
                setRequestTimeFilters(chartMetadata.timeFilters);
            }
        },
        [],
    );

    const setChartColumn = useCallback(
        (chartMetadata: GetDataMakerResponse) => {
            if (chartMetadata?.columns) {
                setChartColumns(
                    chartMetadata.columns.map(
                        (column) => column.originalKey || column.key,
                    ),
                );
            }
        },
        [],
    );

    const chartDataChanges = useCallback(
        (chartMetadata: GetDataMakerResponse) => {
            getTimeFiltersFromMetadata(chartMetadata);
            setChartColumn(chartMetadata);
        },
        [],
    );

    const onApplyChartSettingsSuccess = (
        chartKey: number,
        settings: ChartSettingsT,
    ) =>
        queryClient.setQueryData(
            ['getLayout', slugParam, ''],
            (layout: LayoutT) => {
                return {
                    ...layout,
                    widgets: layout.widgets.map((existingWidget) =>
                        existingWidget.chart?.key === chartKey
                            ? {
                                  ...existingWidget,
                                  chart: {
                                      ...existingWidget.chart,
                                      settings,
                                  },
                              }
                            : existingWidget,
                    ),
                };
            },
        );

    const applyChartSettingsOnChart = useCallback(
        (settings: ChartSettingsT) => {
            if (!isPdfModeActivated)
                updateChartThenUpdateVizSettings({
                    chartData: {
                        ...chart,
                        settings: {
                            ...chart.settings,
                            ...settings,
                        },
                    },
                    chartKey: chart.key,
                    redirect: false,
                    dashboardSlug: slugParam,
                    onSuccess: () =>
                        onApplyChartSettingsSuccess(chart.key, {
                            ...chart.settings,
                            ...settings,
                        }),
                });
        },
        [chart.key, isPdfModeActivated, JSON.stringify(chart)],
    );

    const onDisableChartFiltersSuccess = (
        chartKey: number,
        disableDashboardFilters: boolean,
    ) =>
        queryClient.setQueryData(
            ['getLayout', slugParam, ''],
            (layout: LayoutT) => {
                return {
                    ...layout,
                    widgets: layout.widgets.map((existingWidget) =>
                        existingWidget.chart?.key === chartKey
                            ? {
                                  ...existingWidget,
                                  chart: {
                                      ...existingWidget.chart,
                                      disableDashboardFilters,
                                  },
                              }
                            : existingWidget,
                    ),
                };
            },
        );

    const updateDisableDashboardFiltersOnChart = useCallback(() => {
        // If the user wants to deactivate the dashboard filters, we check if the chart has protected scopes and if the modal is closed before opening it
        if (
            !chart.disableDashboardFilters &&
            protectedScopes.length !== 0 &&
            !noDashboardFiltersModalOpen
        ) {
            setNoDashboardFiltersModalOpen(true);
            return;
        }
        if (!isPdfModeActivated)
            updateChartThenUpdateVizSettings({
                chartData: {
                    disableDashboardFilters: !chart.disableDashboardFilters,
                },
                chartKey: chart.key,
                redirect: false,
                dashboardSlug: slugParam,
                onSuccess: () =>
                    onDisableChartFiltersSuccess(
                        chart.key,
                        !chart.disableDashboardFilters,
                    ),
            });
    }, [
        chart.disableDashboardFilters,
        chart.key,
        isPdfModeActivated,
        noDashboardFiltersModalOpen,
        protectedScopes.length,
    ]);

    const chartEditHeader = (
        <StyChartHeaderButtons hover={hover}>
            {canEdit && (
                <div className="pendo-disable-dashboard-filters-button">
                    <ConditionalTooltipIconButton
                        isActive={chart.disableDashboardFilters}
                        onClick={updateDisableDashboardFiltersOnChart}
                        activeIcon={<FilterAltIcon />}
                        inactiveIcon={<FilterAltOffIcon />}
                        activeTooltip={t('chart.activateDashboardFilters')}
                        inactiveTooltip={t('chart.deactivateDashboardFilters')}
                        iconProps={{ fontSize: 'small' }}
                    />
                </div>
            )}
            {chart.vizType !== 'table' && (
                <ConditionalTooltipIconButton
                    isActive={!forceTable}
                    onClick={() => setForceTable(!forceTable)}
                    activeIcon={<TableChartIcon />}
                    inactiveIcon={vizInfos[chart.vizType].icon}
                    activeTooltip={t('settings.table')}
                    inactiveTooltip={t(`settings.${chart.vizType}`)}
                    iconProps={{ fontSize: 'small' }}
                />
            )}
            {!chart.settings.isMedia && !isPublicDisplay && (
                <StyTopLeftWrapper>
                    <GridChartExportMenu
                        chartTitle={chart.title}
                        disableDashboardFilters={chart.disableDashboardFilters}
                        queryMetrics={chart.metrics}
                        queryDimensions={chart.dimensions}
                        queryFilters={chart.filters}
                        settings={chart.settings}
                        columns={chartColumns}
                        vizType={chart.vizType}
                    />
                </StyTopLeftWrapper>
            )}
            {!isCurrentlyOnPublicDashboard() && (
                <ChartEditMenu
                    chart={chart}
                    chartKey={chart.key}
                    chartTitle={title}
                    canEdit={canEdit}
                    editMode={editMode}
                    toggleEditMode={toggleEditMode}
                />
            )}
        </StyChartHeaderButtons>
    );

    const openIndicator = () => {
        trackEvent('decision/dashboard/open-in-explore-title-click');
    };

    // Show link if client use essential and is admin
    const hasEssential = useIsEssential();
    const isAdminUser = useUserHasRole(ADMIN);
    const isSupportUser = useUserHasRole(SUPPORT);
    const canShowLinkForExploreUser = isExploreUser && !hasEssential;
    const canShowLinkForEssentialAdmin = hasEssential && isAdminUser && canEdit;
    const showLink =
        !hideLink &&
        (isSupportUser ||
            canShowLinkForExploreUser ||
            canShowLinkForEssentialAdmin);
    const getChart = (isPngModeActivated: boolean) => (
        <ChartContainer
            publicToken={publicToken}
            chartKey={chart.key}
            disableDashboardFilters={chart.disableDashboardFilters}
            isChartViewedOnDashboard
            title={chart.title}
            vizType={forceTable ? 'table' : chart.vizType}
            queryMetrics={chart.metrics}
            queryDimensions={chart.dimensions}
            queryFilters={chart.filters}
            settings={chart.settings}
            applyChartSettings={applyChartSettingsOnChart}
            useChartDataMaker={useGetDashboardChartData}
            onChartDataChange={chartDataChanges}
            isPngModeActivated={isPngModeActivated}
            isUpdateChartLoading={isUpdating}
            isPdfModeActivated={isPdfModeActivated}
            isFromArchivedDashboard={isArchived}
        />
    );

    return (
        <Box
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className={`grid-chart ${className}`}
            id={`grid-chart-${chart.key}`}
            data-testid="e2e-grid-chart"
        >
            <StyGridChartCard
                onContextMenu={onRightClick}
                alertTriggered={!!chart.alertTriggered}
            >
                <div className="grid-chart-header">
                    <Flex alignItems="center">
                        {chart.alertTriggered && (
                            <ReportGmailerrorredIcon
                                sx={{
                                    color: MANTY_RED,
                                    fontSize: '40px',
                                    marginRight: '0.5rem',
                                }}
                            />
                        )}
                        <Tooltip
                            title={chart.title}
                            placement="top"
                            enterDelay={100}
                        >
                            <Typography
                                variant="h5"
                                className="chart-title"
                                color={
                                    chart.alertTriggered ? MANTY_RED : 'inherit'
                                }
                            >
                                {showLink && dashboard && !isArchived ? (
                                    <NoColorLink
                                        to={`/explore/${chart.key}${getDashboardParamForExploreRoute(dashboard.slug)}`}
                                        className="chart-title"
                                        data-e2e-title={chart.title}
                                        onClick={openIndicator}
                                    >
                                        {chart.title}
                                    </NoColorLink>
                                ) : (
                                    chart.title
                                )}
                            </Typography>
                        </Tooltip>
                        {!presentationMode && chart.disableDashboardFilters && (
                            <GridChartNoDashboardFilterChip />
                        )}
                    </Flex>
                    {!presentationMode &&
                        !editMode &&
                        !isArchived &&
                        chartEditHeader}
                </div>
                {chart.settings.isMedia && chart.settings.mediaUrl && (
                    <MediaCard
                        mediaUrl={chart.settings.mediaUrl}
                        title={chart.title}
                    >
                        {children}
                    </MediaCard>
                )}
                {!chart.settings.isMedia && (
                    <ErrorBoundary>
                        {getChart(false)}
                        {children}
                        <TimeFilterCard
                            chartKey={chart.key}
                            timeFilters={requestTimeFilters}
                        />
                    </ErrorBoundary>
                )}
                {openContextMenu && canEdit && !isArchived && (
                    <ChartEditMenu
                        chartKey={chart.key}
                        chartTitle={title}
                        canEdit={canEdit}
                        editMode={editMode}
                        toggleEditMode={toggleEditMode}
                        displayMenuOnly
                        anchorPosition={rightClickPosition}
                        onClose={() => setOpenContextMenu(false)}
                    />
                )}
                {showPngExport && (
                    <ChartPngPreview
                        open={showPngExport}
                        title={chart.title}
                        onClose={() => setShowPngExport(false)}
                    >
                        <Box className="explore-viz">{getChart(true)}</Box>
                    </ChartPngPreview>
                )}
            </StyGridChartCard>
            {noDashboardFiltersModalOpen && (
                <GridChartNoDashboardFilterModal
                    open={noDashboardFiltersModalOpen}
                    protectedScopes={protectedScopes}
                    onClose={() => setNoDashboardFiltersModalOpen(false)}
                    onSubmit={updateDisableDashboardFiltersOnChart}
                />
            )}
        </Box>
    );
}

const areItemEqual = (prevProps: Props, nextProps: Props) =>
    prevProps.hideLink === nextProps.hideLink &&
    _.isEqual(prevProps.chart, nextProps.chart) &&
    _.isEqual(prevProps.children, nextProps.children);

const MemoGridChart = React.memo<Props>(GridChart, areItemEqual);

export default MemoGridChart;
